import React, { useCallback, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    LabelList,
    Legend,
} from 'recharts';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import {
    getSubscribersMetrics,
    peekSubscribersMetrics,
} from '../../../utils/subscribersCache';
import EngagementPieChart from '../../../components/Charts/EngagementPieChart';

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
    });
}

/** Short axis/tooltip label for daily chart dates (ISO YYYY-MM-DD). */
function formatChartDay(value) {
    if (value == null || value === '') return '';
    // Numeric day index fallback (legacy cache / old API)
    if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        return String(value);
    }
    const d = new Date(`${value}T12:00:00`);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Convert decimal minutes to mm:ss (e.g. 1.35 → 01:21). */
function formatMinutesToMmSs(minutes) {
    const totalSeconds = Math.max(0, Math.round(Number(minutes) * 60) || 0);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
    });
};
function timeAgo(iso) {
    if (!iso) return 'Never active';
    const diffMs = Date.now('America/Los_Angeles') - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
}

const DURATION_DESCRIPTIONS = [
    { key: 'avg', label: 'Avg', color: '#ec3fa0', desc: 'Average call duration' },
    { key: 'long', label: 'Long', color: '#b23ce0', desc: 'Longest call duration' },
];

const DurationBarLabel = (props) => {
    const { x, y, width, value } = props;
    if (value == null || Number.isNaN(Number(value))) return null;
    const label = formatMinutesToMmSs(value);
    return (
        <text
            x={x + width / 2}
            y={y - 6}
            fill="#fff"
            fontSize={10}
            fontWeight={600}
            textAnchor="middle"
        >
            {label}
        </text>
    );
};

// NEW: label rendered near the bottom/base of each bar
const BarNameLabel = ({ x, y, width, height, name }) => {
    if (height == null || height < 16) return null;
    return (
        <text
            x={x + width / 2}
            y={y + height - 8}
            fill="#fff"
            fontSize={10}
            fontWeight={700}
            textAnchor="middle"
        >
            {name}
        </text>
    );
};

function applySubscribersData(data, setters) {
    if (!data) return;
    setters.setSummary(data.summary ?? null);
    setters.setSignupTrend(data.signupTrend ?? []);
    // setters.setUsersTrend(data.usersTrend ?? []);
    setters.setModalityMix(data.modalityMixdata ?? []);
    setters.setUsers(data.users ?? []);
    setters.setEngagement(data.engagement ?? null);
    setters.setSessionDuration(data.sessionDuration ?? []);
}

export default function MySubscription() {
    const navigate = useNavigate();
    const cached = peekSubscribersMetrics();
    const [summary, setSummary] = useState(() => cached?.summary ?? null);
    const [signupTrend, setSignupTrend] = useState(() => cached?.signupTrend ?? []);
    // const [usersTrend, setUsersTrend] = useState(() => cached?.usersTrend ?? []);
    const [modalityMixdata, setModalityMix] = useState(() => cached?.modalityMixdata ?? []);
    const [sessionDuration, setSessionDuration] = useState(() => cached?.sessionDuration ?? []);
    const [loading, setLoading] = useState(() => !cached);
    const [users, setUsers] = useState(() => cached?.users ?? []);
    const [engagement, setEngagement] = useState(() => cached?.engagement ?? null);
    const [sortBy, setSortBy] = useState('signed_up_pacific');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 8;
    const MODALITY_COLORS = ['#ec3fa0', '#b23ce0', '#f97316'];

    const setters = {
        setSummary,
        setSignupTrend,
        // setUsersTrend,
        setModalityMix,
        setUsers,
        setEngagement,
        setSessionDuration,
    };

    const sortedUsers = [...users].sort((a, b) => {
        const dateA = a?.[sortBy] ? new Date(a[sortBy]).getTime() : 0;
        const dateB = b?.[sortBy] ? new Date(b[sortBy]).getTime() : 0;
        return dateB - dateA; // most recent first
    });

    const totalPages = Math.max(1, Math.ceil(sortedUsers.length / PAGE_SIZE));
    const paginatedUsers = sortedUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    useEffect(() => {
        setPage(1);
    }, [sortBy, users]);

    const loadSubscribers = useCallback(async () => {
        const hit = peekSubscribersMetrics();
        if (hit) {
            applySubscribersData(hit, setters);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await getSubscribersMetrics();
            applySubscribersData(data, setters);
        } catch (err) {
            console.error('Failed to load subscribers metrics', err);
        } finally {
            setLoading(false);
        }
        // setters are stable setState fns
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadSubscribers();
    }, [loadSubscribers]);

    return (
        <>
            <Helmet>
                <title>My Subscriptions | TLK</title>
            </Helmet>
            {/* start: my-subscribers */}
            <div className="main-body">
                <div className="main-header-s1 center-text">
                    <button type="button" className="back-btn" onClick={() => { navigate(-1) }}>
                        <img src="/images/icons/chevron-left.svg" alt="" />
                    </button>
                    <h1 className="main-header-s1__title">My Subscribers</h1>
                    {/* <button type="button" className="transparent-btn">
                        <img src="/images/icons/info.svg" alt="" />
                    </button> */}
                    <div className="main-header-s1__actions"></div>
                </div>
                <div className="body-content">
                    <div className="my-subscribers-layout">

                        <div className="layout-item">
                            <div className="layout-box widget-type2 p-1">
                                <div className="widget-header">
                                    <h2>This Month</h2>
                                </div>
                                <div className="month-stats-row">
                                    <div className="month-stat-card">
                                        <h4>Total</h4>
                                        <h4>Subscribers</h4>
                                        <h2>{loading ? '—' : (summary?.active_subs ?? 0).toLocaleString()}</h2>
                                    </div>
                                    <div className="month-stat-card">
                                        <h4>Total</h4>
                                        <h4>New Users</h4>
                                        <h2>{loading ? '—' : (summary?.signups_this_month ?? 0).toLocaleString()}</h2>
                                    </div>
                                    <div className="month-stat-card">
                                        <h4>Total New</h4>
                                        <h4>Subscribers</h4>
                                        <h2>{loading ? '—' : (summary?.subscribers_this_month ?? 0).toLocaleString()}</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* <div className="layout-item">
                            <div className="layout-box widget-type2">
                                <div className="widget-header">
                                    <h2>User Engagement</h2>
                                    <h4>View your user engagement metrics</h4>
                                </div>
                                {engagement && (
                                    <div className="widget-body">
                                        <div className="earning-metrics-lists">
                                            {Object.entries(engagement).map(([metricKey, metric]) => (
                                                <div className="earning-metrics-item" key={metricKey}>
                                                    <div className="earning-metrics-box">
                                                        <h3 capitalize>{metricKey}</h3>
                                                        <div className="metrics-stats">
                                                            <div className="metrics-stat-row">
                                                                <PhoneOutlinedIcon className="metrics-stat-icon call" fontSize="small" />
                                                                <span>{metric.voice}</span>
                                                            </div>
                                                            <div className="metrics-stat-row">
                                                                <VideocamOutlinedIcon className="metrics-stat-icon video" fontSize="small" />
                                                                <span>{metric.video}</span>
                                                            </div>
                                                            <div className="metrics-stat-row">
                                                                <ChatBubbleOutlineOutlinedIcon className="metrics-stat-icon message" fontSize="small" />
                                                                <span>{metric.chat}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div> */}
                        <EngagementPieChart engagement={engagement} />
                        <div className="layout-item">
                            <div className="layout-box widget-type2">
                                <div className="widget-header">
                                    <h2>New User Conversions</h2>
                                    <h4>Daily, last 30 days</h4>
                                </div>
                                <div className="widget-body chart-panel">
                                    {loading ? (
                                        <div className="chart-loading">Loading…</div>
                                    ) : (
                                        <div className="chart-panel__inner">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={signupTrend} margin={{ top: 12, right: 10, left: 0, bottom: 6 }}>
                                                    <defs>
                                                        <linearGradient id="usersAreaGradient" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#d63384" stopOpacity={0.35} />
                                                            <stop offset="100%" stopColor="#d63384" stopOpacity={0} />
                                                        </linearGradient>
                                                        <linearGradient id="subscribersAreaGradient" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#f06ba8" stopOpacity={0.35} />
                                                            <stop offset="100%" stopColor="#f06ba8" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" vertical={false} />
                                                    <XAxis
                                                        dataKey="day"
                                                        tick={{ fontSize: 10, fill: '#fff' }}
                                                        interval={4}
                                                        minTickGap={18}
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tickMargin={8}
                                                        tickFormatter={formatChartDay}
                                                    />
                                                    <YAxis
                                                        width={28}
                                                        tick={{ fontSize: 10, fill: '#fff' }}
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tickMargin={6}
                                                        allowDecimals={false}
                                                    />
                                                    <Tooltip
                                                        cursor={{ stroke: '#d63384', strokeWidth: 1, strokeDasharray: '4 2' }}
                                                        labelFormatter={formatChartDay}
                                                        contentStyle={{ background: '#000', border: 'none', borderRadius: 8, fontSize: 12 }}
                                                    />
                                                    <Legend
                                                        verticalAlign="top"
                                                        height={28}
                                                        iconSize={8}
                                                        wrapperStyle={{ fontSize: 12 }}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="new_users"
                                                        name="New Users"
                                                        stroke="#b23ce0"
                                                        strokeWidth={2}
                                                        fill="url(#usersAreaGradient)"
                                                        dot={false}
                                                        activeDot={{ r: 4, fill: '#b23ce0', strokeWidth: 0 }}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="subscribers"
                                                        name="New Subscribers"
                                                        stroke="#f06ba8"
                                                        strokeWidth={2}
                                                        fill="url(#subscribersAreaGradient)"
                                                        dot={false}
                                                        activeDot={{ r: 4, fill: '#f06ba8', strokeWidth: 0 }}
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* <div className="layout-item">
                            <div className="layout-box widget-type2">
                                <div className="widget-header">
                                    <h2>New Subscribers Overview</h2>
                                    <h4>Daily, last 30 days</h4>
                                </div>
                                <div className="widget-body chart-panel">
                                    {loading ? (
                                        <div className="chart-loading">Loading…</div>
                                    ) : (
                                        <div className="chart-panel__inner">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={signupTrend} margin={{ top: 12, right: 10, left: 0, bottom: 6 }}>
                                                    <defs>
                                                        <linearGradient id="subsAreaGradient" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#b23ce0" stopOpacity={0.35} />
                                                            <stop offset="100%" stopColor="#b23ce0" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" vertical={false} />
                                                    <XAxis
                                                        dataKey="day"
                                                        tick={{ fontSize: 10, fill: '#fff' }}
                                                        interval={4}
                                                        minTickGap={18}
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tickMargin={8}
                                                        tickFormatter={formatChartDay}
                                                    />
                                                    <YAxis
                                                        width={28}
                                                        tick={{ fontSize: 10, fill: '#fff' }}
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tickMargin={6}
                                                        allowDecimals={false}
                                                    />
                                                    <Tooltip
                                                        cursor={{ stroke: '#b23ce0', strokeWidth: 1, strokeDasharray: '4 2' }}
                                                        formatter={(value) => [value, 'subscribers']}
                                                        labelFormatter={formatChartDay}
                                                        contentStyle={{ background: '#fff', border: 'none', borderRadius: 8, fontSize: 12 }}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="subscribers"
                                                        stroke="#b23ce0"
                                                        strokeWidth={2}
                                                        fill="url(#subsAreaGradient)"
                                                        dot={false}
                                                        activeDot={{ r: 4, fill: '#b23ce0', strokeWidth: 0 }}
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div> */}
                        {/* Modality Mix */}
                        {/* <div className="layout-item">
                            <div className="layout-box widget-type2">
                                <div className="widget-header">
                                    <h2>Modality Mix</h2>
                                    <h4>Share of chat / voice / video</h4>
                                </div>
                                <div className="widget-body chart-panel">
                                    <div className="chart-panel__inner" style={{ height: 168 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={modalityMixdata}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={45}
                                                    outerRadius={68}
                                                    dataKey="value"
                                                    paddingAngle={4}
                                                >
                                                    {modalityMixdata?.map((_, i) => (
                                                        <Cell key={i} fill={MODALITY_COLORS[i]} stroke="transparent" />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    cursor={false}
                                                    formatter={(v) => [`${v}%`, '']}
                                                    contentStyle={{ background: '#fff', border: 'none', borderRadius: 8, fontSize: 12 }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: 14, paddingTop: 4 }}>
                                        {modalityMixdata?.map((d, i) => (
                                            <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: MODALITY_COLORS[i], display: 'inline-block' }} />
                                                <span style={{ fontSize: 11, color: '#fff' }}>{d.name} {d.value}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div> */}

                        {/* Session Duration */}
                        <div className="layout-item">
                            <div className="layout-box widget-type2">
                                <div className="widget-header">
                                    <h2>Video AND Voice Duration</h2>
                                    <h4>How long your fans are chatting</h4>
                                </div>
                                <div className="widget-body chart-panel chart-panel--short">
                                    <div className="chart-panel__inner">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={sessionDuration}
                                                barCategoryGap="28%"
                                                barGap={6}
                                                margin={{ top: 22, right: 10, left: 8, bottom: 4 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                                                <XAxis
                                                    dataKey="mode"
                                                    tick={{ fontSize: 11, fill: '#fff', fontWeight: 600 }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tickMargin={8}
                                                />
                                                <YAxis
                                                    width={44}
                                                    tick={{ fontSize: 10, fill: '#fff' }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tickMargin={4}
                                                    tickFormatter={formatMinutesToMmSs}
                                                    allowDecimals={false}
                                                />
                                                <Bar
                                                    dataKey="avg"
                                                    name="Avg"
                                                    fill="#ec3fa0"
                                                    radius={[6, 6, 0, 0]}
                                                    barSize={28}
                                                    isAnimationActive={false}
                                                >
                                                    <LabelList dataKey="avg" content={DurationBarLabel} />
                                                    {/* <LabelList dataKey="avg" content={(props) => <BarNameLabel {...props} name="Avg" />} /> */}
                                                </Bar>
                                                <Bar
                                                    dataKey="longest_min"
                                                    name="Longest min"
                                                    fill="#b23ce0"
                                                    radius={[6, 6, 0, 0]}
                                                    barSize={28}
                                                    isAnimationActive={false}
                                                >
                                                    <LabelList dataKey="longest_min" content={DurationBarLabel} />
                                                    {/* <LabelList dataKey="longest_min" content={(props) => <BarNameLabel {...props} name="Long" />} /> */}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                <div className="session-duration-legend">
                                    {DURATION_DESCRIPTIONS.map((item) => (
                                        <div key={item.key} className="session-duration-legend-item">
                                            <span className="legend-label" style={{ color: item.color }}>{item.label}</span>
                                            <span className="legend-desc">{item.desc}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {/* <div className="layout-item">
                            <div className="layout-box widget-type2">
                                <div className="widget-header">
                                    <h2>Top Sources</h2>
                                    <h4>Where your subscribers are coming from</h4>
                                </div>
                                <div className="widget-body coming-soon-wrap">
                                <img
                                    src="/images/top-sources-chart.png"
                                    alt=""
                                    className="blur-content"
                                />
                                <div className="coming-soon-badge">Coming Soon</div>
                                </div>
                            </div>
                        </div> */}
                        {/* User Stats Table */}
                        {/* User Stats Table */}
                        <div className="layout-item">
                            <div className="layout-box widget-type2">
                                <div className="widget-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <h2>User Stats</h2>
                                        <h4>Engagement breakdown per user</h4>
                                    </div>
                                    {/* <select
                                        className="form-control select1"
                                        style={{ width: 150, height: 28, fontSize: 11, padding: '0 6px' }}
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="signed_up_pacific">Sort: Signed up</option>
                                        <option value="last_activity_pacific">Sort: Last activity</option>
                                    </select> */}
                                </div>
                                <div className="widget-body user-table-wrap">
                                    {loading ? (
                                        <div className="chart-loading">Loading…</div>
                                    ) : users.length === 0 ? (
                                        <div className="empty-text">No users yet.</div>
                                    ) : (
                                        <>
                                            {/* Desktop table */}
                                            <div className="user-table-scroll">
                                                <table className="user-stats-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Email</th>
                                                            <th>Signed up</th>
                                                            <th>Chat</th>
                                                            <th>Voice Min</th>
                                                            <th>Video Min</th>
                                                            <th>48h Messages</th>
                                                            <th>Last Activity</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedUsers.map((u) => (
                                                            <tr key={u.user_id}>
                                                                <td>{u.first_name || 'Unnamed user'} {u?.last_name}</td>
                                                                <td>{u.email || '—'}</td>
                                                                <td>{formatDate(u?.signed_up_pacific)}</td>
                                                                <td>{u.chat_msgs ?? 0}</td>
                                                                <td>{u.voice_min ?? 0}m</td>
                                                                <td>{u.video_min ?? 0}m</td>
                                                                <td>{u.msgs_48h ?? 0}</td>
                                                                <td>{fmtDate(u.last_activity_pacific)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Mobile cards */}
                                            {/* <div className="user-card-list">
                                                {paginatedUsers.map((u) => (
                                                    <div key={u.user_id} className="user-card">
                                                        <div className="user-card-top">
                                                            <div className="user-card-name">{u.first_name?.trim() || 'Unnamed user'} {u?.last_name}</div>
                                                            <div className="user-card-time">{timeAgo(u.last_active_pacific)}</div>
                                                        </div>
                                                        <div className="user-card-email">{u.email || '—'}</div>
                                                        <div className="user-card-stats">
                                                            <div className="user-card-stat">
                                                                <span className="stat-label">Signed up</span>
                                                                <span className="stat-value">{formatDate(u?.signed_up_pacific)}</span>
                                                            </div>
                                                            <div className="user-card-stat">
                                                                <span className="stat-label">Chat</span>
                                                                <span className="stat-value">{u.total_messages ?? 0}</span>
                                                            </div>
                                                            <div className="user-card-stat">
                                                                <span className="stat-label">Voice</span>
                                                                <span className="stat-value">{u.voice_min ?? 0}m</span>
                                                            </div>
                                                            <div className="user-card-stat">
                                                                <span className="stat-label">Video</span>
                                                                <span className="stat-value">{u.video_min ?? 0}m</span>
                                                            </div>
                                                            <div className="user-card-stat">
                                                                <span className="stat-label">48h Msgs</span>
                                                                <span className="stat-value">{u.messages_48h ?? 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div> */}

                                            {/* Pagination */}
                                            <div className="user-table-pagination">
                                                <button
                                                    type="button"
                                                    className="page-btn"
                                                    disabled={page === 1}
                                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                                >
                                                    Prev
                                                </button>
                                                <span className="page-info">
                                                    Page {page} of {totalPages}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="page-btn"
                                                    disabled={page === totalPages}
                                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* <div className="bottom-cpx-text">Metrics update every 24 hours.</div> */}
            </div>
            {/* end: my-subscribers */}
        </>
    );
}